// client/src/components/labEngine/NotebookCell.jsx

import React, { useRef, useState, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { usePyodide } from './PyodideContext';
import { useNodeJS }  from './NodeJSContext';
import { Play, Trash2, Plus, GripVertical } from 'lucide-react';
import CustomAlert from '../customs/customAlert';
import { validatePinInputs } from '../../utilities/validations/notebookCodeValidation';
import showNotification from '../../utilities/showNotification';
import './notebookCell.css';

const FILE_UPLOAD_COMPONENT_IDS = ['AI_DATA_CSV', 'AI_DATA_IMAGES'];
const IMAGE_COMPONENT_IDS       = ['AI_DATA_IMAGES'];

const CATEGORY_TAGS = {
  data:          { bg: '#E6F1FB', color: '#185FA5', label: 'data' },
  preprocessing: { bg: '#E1F5EE', color: '#0F6E56', label: 'preprocess' },
  model:         { bg: '#EEEDFE', color: '#534AB7', label: 'model' },
  training:      { bg: '#FAECE7', color: '#993C1D', label: 'train' },
  evaluation:    { bg: '#FAEEDA', color: '#854F0B', label: 'eval' },
  output:        { bg: '#EAF3DE', color: '#3B6D11', label: 'output' },
};

// Key part: letters/digits/spaces/underscore/slash/hyphen/parens, up to 60
// chars — wide enough for keys like "LCEL Chain response (invoke_singleinput)".
// const STDOUT_KV_RE           = /^([A-Za-z][A-Za-z0-9 _/()-]{1,60}):\s?(.*)$/;
const STDOUT_KV_RE           = /^([^:\n]{1,80}):\s?(.*)$/;
const STDOUT_NUMBER_RE       = /^(\d+)\.\s+(.+)$/;
// "  [0] system: text" — indented bracketed-index sub-item (enumerate() output)
const STDOUT_BRACKET_RE      = /^(\s*)\[(\d+)\]\s*([A-Za-z][A-Za-z0-9 _]{0,30}):\s*(.*)$/;
// "      human:  text" — indented plain key:value continuation line
const STDOUT_INDENTED_KV_RE  = /^(\s{2,})([A-Za-z][A-Za-z0-9 _]{0,30}):\s*(.*)$/;
// A value or whole line that is itself a JSON object/array
const JSON_SHAPE_RE          = /^[{[][\s\S]*[}\]]$/;
const STDOUT_HEADER_KEYS = new Set(['answer', 'question', 'chain', 'retrieval mode']);

// Best-effort pretty-print: if it parses as JSON, re-stringify with indent;
// otherwise return the original text untouched (never throw on malformed/
// truncated JSON-looking fragments — e.g. output cut off by a slice()).
function tryPrettyJson(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

// Renders a JSON blob with each line's "key": portion styled distinctly
// from its value — i.e. everything from the start of the line up through
// the colon gets its own span, per your ask.
function JsonBlock({ text }) {
  const pretty = tryPrettyJson(text);
  const lines = pretty.split('\n');
  return (
    <div className="nb-output__json">
      {lines.map((line, i) => {
        const m = line.match(/^(\s*)"([^"]+)":\s?(.*)$/);
        if (m) {
          const [, indent, key, rest] = m;
          return (
            <div key={i} className="nb-output__json-line">
              {indent}
              <span className="nb-output__json-key">&quot;{key}&quot;:</span>
              {rest && <span className="nb-output__json-value"> {rest}</span>}
            </div>
          );
        }
        return <div key={i} className="nb-output__json-line">{line}</div>;
      })}
    </div>
  );
}

function renderStdoutLine(line, i) {
  if (!line.trim()) {
    return <div key={i} className="nb-output__stdout-spacer" />;
  }

  const bracketMatch = line.match(STDOUT_BRACKET_RE);
  if (bracketMatch) {
    const [, , idx, key, value] = bracketMatch;
    return (
      <div key={i} className="nb-output__subitem">
        <span className="nb-output__subitem-idx">[{idx}]</span>
        <span className="nb-output__subitem-key">{key}:</span>
        <span className="nb-output__subitem-value">{value}</span>
      </div>
    );
  }

  const indentedKvMatch = line.match(STDOUT_INDENTED_KV_RE);
  if (indentedKvMatch) {
    const [, , key, value] = indentedKvMatch;
    return (
      <div key={i} className="nb-output__subitem nb-output__subitem--plain">
        <span className="nb-output__subitem-key">{key}:</span>
        <span className="nb-output__subitem-value">{value}</span>
      </div>
    );
  }

  // const kvMatch = line.match(STDOUT_KV_RE);
  // if (kvMatch) {
  //   const [, key, value] = kvMatch;
  //   const isHeader = STDOUT_HEADER_KEYS.has(key.trim().toLowerCase());
  //   const trimmedValue = value.trim();
  //   const valueIsJson = trimmedValue !== '' && JSON_SHAPE_RE.test(trimmedValue);
  //   return (
  //     <div key={i} className={`nb-output__kv${isHeader ? ' nb-output__kv--header' : ''}`}>
  //       <span className="nb-output__kv-key">{key}:</span>
  //       {valueIsJson
  //         ? <JsonBlock text={trimmedValue} />
  //         : (value && <span className="nb-output__kv-value"> {value}</span>)}
  //     </div>
  //   );
  // }

  const kvMatch = line.match(STDOUT_KV_RE);
  if (kvMatch) {
    const [, key, value] = kvMatch;
    const keyTrim = key.trim();
    const isHeader = STDOUT_HEADER_KEYS.has(keyTrim.toLowerCase());
    const isResponse = /response/i.test(keyTrim);
    const trimmedValue = value.trim();
    const valueIsJson = trimmedValue !== '' && JSON_SHAPE_RE.test(trimmedValue);
    const kvClass = [
      'nb-output__kv',
      isHeader && 'nb-output__kv--header',
      isResponse && 'nb-output__kv--response',
    ].filter(Boolean).join(' ');
    return (
      <div key={i} className={kvClass}>
        <span className={`nb-output__kv-key${isResponse ? ' nb-output__kv-key--response' : ''}`}>{key}:</span>
        {valueIsJson
          ? <JsonBlock text={trimmedValue} />
          : (value && <span className="nb-output__kv-value"> {value}</span>)}
      </div>
    );
  }

  const numMatch = line.match(STDOUT_NUMBER_RE);
  if (numMatch) {
    const [, num, text] = numMatch;
    return (
      <div key={i} className="nb-output__list-item">
        <span className="nb-output__list-num">{num}.</span>
        <span className="nb-output__list-text">{text}</span>
      </div>
    );
  }

  // A bare line that is itself a JSON object/array (no "Key:" prefix at all)
  if (JSON_SHAPE_RE.test(line.trim())) {
    return <JsonBlock key={i} text={line.trim()} />;
  }

  return <div key={i} className="nb-output__stdout-line">{line}</div>;
}

const NotebookCell = ({
  id,
  index,
  simulatorId = null,
  category = '',
  initialCode = '',
  pythonCode = '',
  javascriptCode = '',
  domain = 'AI',
  executionContext = 'pyodide',
  simOutputs = [],
  tag = null,
  label = '',
  componentId = '',
  savedCode = '',
  onSaveCell,
  onRegenerateCell,
  isRegenerating = false,
  onDelete,
  onInsertBelow,
  onCodeChange,
  onMove,
  isLast,
  onCellOutput,
  onEditorMount,
  onTranslateCell,
  generationStatus = 'done',        
  generationError  = '',            
  onRetryGenerateCell, 
}) => {
  const pyodideCtx = usePyodide();
  const nodejsCtx  = useNodeJS();

  // const activeCtx = domain === 'AgenticAI' ? nodejsCtx : pyodideCtx;
  // const runCode   = activeCtx?.runCode;
  // const pyLoading = activeCtx?.isLoading ?? false;
  // const pyodide   = domain === 'AgenticAI' ? null : pyodideCtx?.pyodide;

  const activeCtx = executionContext === 'nodejs' ? nodejsCtx : pyodideCtx;
  const runCode   = activeCtx?.runCode;
  const pyLoading = activeCtx?.isLoading ?? false;
  const pyodide   = executionContext === 'nodejs' ? null : pyodideCtx?.pyodide;

  // const pyodide   = pyodideCtx?.pyodide;
  // const { runCode, isLoading: pyLoading } = { runCode: pyodideCtx.runCode, isLoading: pyodideCtx.isLoading };
  
  const [output,    setOutput]    = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runCount,  setRunCount]  = useState(null);
  const [isActive,  setIsActive]  = useState(false);
  const [testFiles, setTestFiles]             = useState([]);
  const [showTestMenu, setShowTestMenu]       = useState(false);
  const [selectedTestFile, setSelectedTestFile] = useState(null);
  const [alertVisible, setAlertVisible]       = useState(false);
  const [alertConfig,  setAlertConfig]        = useState({});
  // Language toggle — AgenticAI only
  const [displayLang, setDisplayLang] = useState('python');
  const [jsCode, setJsCode] = useState(javascriptCode);
  const [isTranslating, setIsTranslating] = useState(false);
  
  const resolvedTag = CATEGORY_TAGS[category] || tag || null;
  
  const lastTranslatedPythonRef = useRef(initialCode);
  const displayLangRef = useRef('python');
  const codeRef   = useRef(initialCode);
  const editorRef = useRef(null);

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    onEditorMount?.(editor);
    const updateHeight = () => {
      const lineCount = editor.getModel()?.getLineCount() || 1;
      const height = Math.max(lineCount * 20 + 20, 60);
      editor.layout({ height, width: editor.getLayoutInfo().width });
    };
    editor.onDidChangeModelContent(updateHeight);
    updateHeight();
  };

  const pushCodeToEditor = (newCode) => {
    codeRef.current = newCode;
    onCodeChange(id, newCode);
    if (editorRef.current) {
      const model = editorRef.current.getModel();
      if (model) model.setValue(newCode);
    }
  };

    const executeRun = async (currentCode, overrideJs) => {
    // AgenticAI: always execute JS version regardless of display language
    const codeToRun = executionContext === 'nodejs' ? (overrideJs ?? jsCode ?? currentCode) : currentCode;
    console.log('🐛 [executeRun] executionContext:', executionContext);
    console.log('🐛 [executeRun] jsCode is empty?', !jsCode, '| jsCode length:', jsCode?.length);
    console.log('🐛 [executeRun] currentCode length:', currentCode?.length);
    console.log('🐛 [executeRun] codeToRun first 200 chars:', codeToRun?.slice(0, 200));
    
    const allCodes = window.__notebookCellCodes || [];

    const currentIndex = allCodes.findIndex(c => c.id === id);
    if (currentIndex > 0) {
      const previousCodes = allCodes.slice(0, currentIndex).map(c => c.code);
      const validationErrors = validatePinInputs(currentCode, previousCodes, currentIndex);
      if (validationErrors.length > 0) {
        setOutput({ stdout: '', plot: null, error: validationErrors.join('\n') });
        return;
      }
    }
    setIsRunning(true);
    setOutput(null);
    try {
      const result = await runCode(codeToRun, id, simulatorId);
      setOutput(result);
      setRunCount(prev => (prev ?? 0) + 1);
      onCellOutput?.(id, componentId, result);
      // Persist the freshly-translated JS (if this was a nodejs run) so the
      // next session doesn't have to pay for another translation call.
      onSaveCell?.(id, currentCode, result, executionContext === 'nodejs' ? codeToRun : undefined);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRun = useCallback(async () => {
    if (isRunning || pyLoading || isTranslating) return;

    if (executionContext === 'nodejs') {
      // Run never generates or translates — it only executes whatever
      // JavaScript already exists. If there's none, or the Python has
      // changed since the last Translate, tell the user to translate first
      // instead of silently paying for another AI call.
      if (!jsCode) {
        showNotification('No JavaScript version yet — click Translate first.', 'error');
        return;
      }
      const pythonUnchanged = codeRef.current === lastTranslatedPythonRef.current;
      if (!pythonUnchanged) {
        showNotification('Python has changed since the last translation — click Translate before running.', 'error');
        return;
      }
      await executeRun(codeRef.current, jsCode);
      return;
    }

    executeRun(codeRef.current);
  }, [isRunning, pyLoading, isTranslating, jsCode, executionContext]);

  const handleSave = () => {
    onSaveCell?.(id, codeRef.current);
  };

  const handleTranslate = async () => {
    if (isTranslating) return;
    setIsTranslating(true);
    try {
      // const response = await onTranslateCell?.(id, codeRef.current);
      const response = await onTranslateCell?.(id, codeRef.current, componentId);
      if (response?.success) {
        setJsCode(response.data.javascriptCode);
        lastTranslatedPythonRef.current = codeRef.current;
        // Persist immediately — otherwise a manual Translate that's never
        // followed by Run is lost the moment the user navigates away, and
        // gets silently re-translated (another gpt-4o call) next session.
        onSaveCell?.(id, codeRef.current, null, response.data.javascriptCode);
        showNotification('JavaScript version updated!', 'success');
      } else {
        showNotification(response?.error || 'Translation failed', 'error');
      }
    } catch (err) {
      showNotification('Translation failed', 'error');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;
    const uploadedName = file.name;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const content = new Uint8Array(evt.target.result);
      pyodide.FS.writeFile(`/home/pyodide/${uploadedName}`, content);
      const updatedCode = codeRef.current.replace(
        /FILE_PATH\s*=\s*['"][^'"]*['"]/,
        `FILE_PATH = '/home/pyodide/${uploadedName}'`
      );
      pushCodeToEditor(updatedCode);
      onSaveCell?.(id, updatedCode);
      setSelectedTestFile(uploadedName);
    };

    if (configuredPath && !configuredPath.endsWith(uploadedName)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Configured path: ${configuredPath}\nUploaded file: ${uploadedName}\n\nUpdate FILE_PATH to use "${uploadedName}"?`,
        onConfirm: () => {
          setAlertVisible(false);
          reader.readAsArrayBuffer(file);
        }
      });
      setAlertVisible(true);
      return;
    }

    reader.readAsArrayBuffer(file);
  };

  const handleTestFileSelect = async (filename) => {
    setShowTestMenu(false);

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;

    if (configuredPath && !configuredPath.endsWith(filename)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Current path: ${configuredPath}\nSelected file: ${filename}\n\nUpdate FILE_PATH to use "${filename}"?`,
        onConfirm: async () => {
          setAlertVisible(false);
          const res = await fetch(`/aitestdata/${filename}`);
          const text = await res.text();
          const encoder = new TextEncoder();
          pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
          const updatedCode = codeRef.current.replace(
            /FILE_PATH\s*=\s*['"][^'"]*['"]/,
            `FILE_PATH = '/home/pyodide/${filename}'`
          );
          pushCodeToEditor(updatedCode);
          onSaveCell?.(id, updatedCode);
          setSelectedTestFile(filename);
        }
      });
      setAlertVisible(true);
      return;
    }

    const res = await fetch(`/aitestdata/${filename}`);
    const text = await res.text();
    const encoder = new TextEncoder();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
    const updatedCode = codeRef.current.replace(
      /FILE_PATH\s*=\s*['"][^'"]*['"]/,
      `FILE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  };

  const handleTestDataClick = async () => {
    if (!showTestMenu) {
      try {
        const indexFile = IMAGE_COMPONENT_IDS.includes(componentId)
          ? '/aitestdata/images-index.json'
          : '/aitestdata/index.json';
        const res = await fetch(indexFile);
        const files = await res.json();
        setTestFiles(files);
      } catch {
      setTestFiles(IMAGE_COMPONENT_IDS.includes(componentId)
        ? ['cat.jpg', 'dog.jpg']
        : ['iris_test.csv', 'housing.csv', 'titanic.csv']);
      }
    }
    setShowTestMenu(prev => !prev);
  };

  const handleTestImageSelect = async (filename) => {
    setShowTestMenu(false);
    const res = await fetch(`/aitestdata/images/${filename}`);
    const buf = await res.arrayBuffer();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, new Uint8Array(buf));
    const updatedCode = codeRef.current.replace(
      /IMAGE_PATH\s*=\s*['"][^'"]*['"]/,
      `IMAGE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const hasOutput = output && (output.stdout || output.plot || output.error);

  // ── Cell-by-cell generation states — rendered instead of the full editor
  // until this cell's Python has actually been generated (or fails).
  if (generationStatus === 'pending') {
    return (
      <div className="nb-cell nb-cell--pending">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[ ]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          ⏳ Waiting to generate…
        </div>
      </div>
    );
  }

  if (generationStatus === 'generating') {
    return (
      <div className="nb-cell nb-cell--generating">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[*]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          <div className="nb-running__dot" />
          <div className="nb-running__dot" />
          <div className="nb-running__dot" />
          <span className="nb-running__label">🤖 Generating…</span>
        </div>
      </div>
    );
  }

  if (generationStatus === 'error') {
    return (
      <div className="nb-cell nb-cell--generation-error">
        <div className="nb-toolbar">
          <span className="nb-toolbar__run-count">[!]</span>
          {resolvedTag && (
            <span className="nb-badge" style={{ background: resolvedTag.bg, color: resolvedTag.color }}>
              {resolvedTag.label}
            </span>
          )}
          <span className="nb-label">{label || 'Untitled cell'}</span>
        </div>
        <div className="nb-cell__generation-placeholder">
          <pre className="nb-output__error">❌ {generationError || 'Generation failed'}</pre>
          <button
            onClick={() => onRetryGenerateCell?.(id)}
            className="nb-btn--run"
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      className={`nb-cell ${isActive ? 'nb-cell--active' : ''}`}
    >
      {/* ── Cell toolbar ─────────────────────────────────────────────────── */}
      <div className="nb-toolbar">
        <GripVertical size={14} className="nb-toolbar__grip" />

        {/* Move up/down */}
        {index > 0 && (
          <button
            onClick={() => onMove(id, 'up')}
            title="Move cell up"
            className="nb-btn--icon nb-btn--move"
          >
            ▲
          </button>
        )}
        {!isLast && (
          <button
            onClick={() => onMove(id, 'down')}
            title="Move cell down"
            className="nb-btn--icon nb-btn--move"
          >
            ▼
          </button>
        )}

        <span className="nb-toolbar__run-count">
          {isRunning ? '[*]' : runCount !== null ? `[${runCount}]` : '[ ]'}
        </span>

        {resolvedTag && (
          <span
            className="nb-badge"
            style={{ background: resolvedTag.bg, color: resolvedTag.color }}
          >
            {resolvedTag.label}
          </span>
        )}

        <span className="nb-label">
          {label || (executionContext === 'nodejs' ? 'JavaScript' : 'Python')}
        </span>

        {/* Language toggle — AgenticAI only */}
        {/* {domain === 'AgenticAI' && ( */}
        {executionContext === 'nodejs' && (
          <div className="nb-lang-toggle">
          <button
              className={`nb-btn--lang ${displayLang === 'python' ? 'active' : ''}`}
              onClick={() => {
                displayLangRef.current = 'python';
                setDisplayLang('python');
                const model = editorRef.current?.getModel();
                if (model) model.setValue(codeRef.current);
              }}
              title="Show Python code"
            >
              🐍 Python
            </button>
            <button
              className={`nb-btn--lang ${displayLang === 'javascript' ? 'active' : ''}`}
              onClick={() => {
                displayLangRef.current = 'javascript';
                setDisplayLang('javascript');
                const model = editorRef.current?.getModel();
                if (model) model.setValue(jsCode || '// JavaScript not yet generated');
              }}
              title="Show JavaScript code"
            >
              📜 JS
            </button>
          </div>
        )}

        <div className="nb-toolbar__actions">

          {/* File upload — only for data source cells */}
          {FILE_UPLOAD_COMPONENT_IDS.includes(componentId) && (
            <>
              {/* Test data dropdown */}
              <div className="nb-test-data-wrapper">
                <button onClick={handleTestDataClick} className="nb-btn--test-data">
                  {selectedTestFile ? `📄 ${selectedTestFile} ▾` : '💾 Test Data ▾'}
                </button>
                {showTestMenu && testFiles.length > 0 && (
                  <div className="nb-test-menu">
                    {testFiles.map(f => (
                      <div
                        key={f}
                        className="nb-test-menu__item"
                        onClick={() =>
                          IMAGE_COMPONENT_IDS.includes(componentId)
                            ? handleTestImageSelect(f)
                            : handleTestFileSelect(f)
                        }
                      >
                        📄 {f}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload CSV/Image */}
              <label className="nb-btn--upload">
                {IMAGE_COMPONENT_IDS.includes(componentId) ? '🖼️ Upload Image' : '📂 Upload CSV'}
                <input
                  type="file"
                  accept={IMAGE_COMPONENT_IDS.includes(componentId) ? 'image/*' : '.csv'}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </label>
            </>
          )}

          {/* Regenerate */}
          <button
            onClick={() => onRegenerateCell?.(id)}
            title="Regenerate cell code from pipeline configuration"
            className="nb-btn--icon nb-btn--regenerate"
            disabled={isRegenerating}
          >
            {isRegenerating ? '⏳ Regenerating' : '🔄 1. Regenerate'}
          </button>

          {/* Translate — AgenticAI only */}
          {executionContext === 'nodejs' && (
            <button
              onClick={handleTranslate}
              disabled={isTranslating}
              title="Translate Python to JavaScript"
              className="nb-btn--icon nb-btn--translate"
            >
              {isTranslating ? '⏳ Translating' : '🔄 2. Translate'}
            </button>
          )}

          {/* Run cell */}
          <button
            onClick={handleRun}
            disabled={isRunning || pyLoading}
            title="Run cell (Shift+Enter)"
            className="nb-btn--run"
          >
            <Play size={12} />
            {isRunning ? 'Running...' : '3. Run'}
          </button>

          {/* Save */}
          <button onClick={handleSave} title="Save cell code" className="nb-btn--icon nb-btn--save">
            💾
          </button>

          {/* Insert cell below */}
          <button
            onClick={() => onInsertBelow(id)}
            title="Insert cell below"
            className="nb-btn--icon nb-btn--insert"
          >
            <Plus size={12} />
          </button>

          {/* Delete cell */}
          <button
            onClick={() => onDelete(id)}
            title="Delete cell"
            className="nb-btn--icon nb-btn--delete"
          >
            <Trash2 size={12} />
          </button>

          {isRegenerating && (
            <div className="ai-loading-overlay">
              <div className="ai-loading-spinner" />
              <p className="ai-loading-title">
                🤖 Prof-iX is re-generating your code...
              </p>
              <p className="ai-loading-subtitle">
                This may take a few seconds
              </p>
            </div>
          )}

        </div>
      </div>

      {/* ── Monaco Editor ─────────────────────────────────────────────────── */}
      <div className="nb-editor" onKeyDown={handleKeyDown}>
        <Editor
          defaultLanguage="python"
          language={displayLang === 'javascript' ? 'javascript' : 'python'}
          defaultValue={initialCode}
          theme="vs-dark"
          onMount={handleEditorMount}
          onChange={(value) => {
            if (displayLangRef.current === 'javascript') {
              setJsCode(value || '');
            } else {
              codeRef.current = value || '';
              onCodeChange(id, value || '');
            }
          }}
          options={{
            minimap:              { enabled: false },
            scrollBeyondLastLine: false,
            fontSize:             13,
            lineHeight:           20,
            padding:              { top: 10, bottom: 10 },
            lineNumbers:          'on',
            folding:              false,
            renderLineHighlight:  'line',
            scrollbar:            { vertical: 'hidden', horizontal: 'hidden' },
            overviewRulerLanes:   0,
            automaticLayout:      true,
            tabSize:              4,
            wordWrap:             'on',
          }}
        />
      </div>

      {(isTranslating || isRunning) && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-spinner" />
          <p className="ai-loading-title">
            🤖 Prof-iX is {isTranslating ? 'Translating to JavaScript…' : 'Running…'}
          </p>
          <p className="ai-loading-subtitle">This may take a few seconds</p>
        </div>
      )}

      {/* ── Running indicator ─────────────────────────────────────────────── */}
      {isRunning && (
        <div className="nb-running">
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__label">Executing…</span>
        </div>
      )}

      {/* ── Output area ───────────────────────────────────────────────────── */}
      {(hasOutput || (isRunning === false && output !== null)) && (
        <div className="nb-output">

          {/* Expected output label pills */}
          {simOutputs.length > 0 && (
            <div className="nb-output__labels">
              {simOutputs.map((out, i) => (
                <span key={i} className="nb-output__label-pill">
                  {out.name}
                  {out.unit    ? ` (${out.unit})`    : ''}
                  {out.dataType ? ` · ${out.dataType}` : ''}
                </span>
              ))}
            </div>
          )}

          {/* Actual output */}
          <div className={`nb-output__content ${output?.error ? 'nb-output__content--error' : ''}`}>

            {output?.stdout && (
              <div className="nb-output__stdout">
                {output.stdout.split('\n').map(renderStdoutLine)}
              </div>
            )}

            {output?.error && (
              <pre className="nb-output__error">{output.error}</pre>
            )}

            {output?.plot && (
              <img
                src={`data:image/png;base64,${output.plot}`}
                alt="Plot output"
                className={`nb-output__plot ${output?.stdout ? 'nb-output__plot--with-stdout' : ''}`}
              />
            )}

            {!output?.error && !output?.stdout && !output?.plot && output !== null && (
              <span className="nb-output__empty">(no output)</span>
            )}

          </div>
        </div>
      )}

      <CustomAlert
        visible={alertVisible}
        title={alertConfig.title}
        icon={alertConfig.icon}
        message={alertConfig.message}
        onConfirm={alertConfig.onConfirm}
        onCancel={() => setAlertVisible(false)}
      />

    </div>
  );
};

export default NotebookCell;
